import StarIcon from "@material-ui/icons/StarBorder"
import { Dialog, Slide } from "@mui/material"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardActions from "@mui/material/CardActions"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import Container from "@mui/material/Container"
import CssBaseline from "@mui/material/CssBaseline"
import GlobalStyles from "@mui/material/GlobalStyles"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import { getAnalytics, logEvent } from "firebase/analytics"
import * as React from "react"
import { FunctionComponent, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Color from "../Color"
import Dimen from "../Dimen"
import { Caller, Product } from "../Enum"
import BKashDialog from "./BKashDialog"
import { urlGooglePlay } from "./HomePage"

const isFreeSubscription = (title: String): Boolean => {
  return title === "ফ্রী" || title === "Free"
}

const isProSubscription = (title: String): Boolean => {
  return title === "প্রো" || title === "Pro"
}

const Pricing: FunctionComponent<{ caller?: Caller; email?: String }> = ({
  caller = Caller.Homepage,
  email = "",
}) => {
  const intl = useIntl()
  const analytics = getAnalytics()

  const [openBKashDialog, setOpenBKashDialog] = useState<boolean>(false)
  const [selectedProduct, setSelectedProduct] = useState<Product>(Product.Pro)

  // Display Free version ONLY on homepage
  const tiers = [
    ...(caller === Caller.Homepage
      ? [
          // Free
          {
            title: intl.formatMessage({
              id: "subscription.free",
            }),
            priceLifetime: intl.formatMessage({
              id: "subscription.free.price",
            }),
            description: [
              intl.formatMessage({
                id: "subscription.free.bulletpoint.one",
              }),
              intl.formatMessage({
                id: "subscription.free.bulletpoint.two",
              }),
            ],
            buttonText: intl.formatMessage({
              id: "subscription.free.button",
            }),
            buttonText2: intl.formatMessage({
              id: "subscription.free.button.two",
            }),
            buttonVariant: "outlined",
          },
        ]
      : []),
    // Adfree
    {
      title: intl.formatMessage({
        id: "subscription.adfree",
      }),
      priceLifetime: intl.formatMessage({
        id: "subscription.adfree.price",
      }),
      description: [
        intl.formatMessage({
          id: "subscription.adfree.bulletpoint.one",
        }),
        intl.formatMessage({
          id: "subscription.adfree.bulletpoint.two",
        }),
      ],
      buttonText: intl.formatMessage({
        id: "subscription.adfree.button",
      }),
      buttonText2: intl.formatMessage({
        id: "subscription.adfree.button.two",
      }),
      buttonVariant: "outlined",
    },
    // Pro
    {
      title: intl.formatMessage({
        id: "subscription.pro",
      }),
      subheader: intl.formatMessage({
        id: "subscription.pro.subtitle",
      }),
      priceMonthly: intl.formatMessage({
        id: "subscription.pro.price",
      }),
      priceLifetime: intl.formatMessage({
        id: "subscription.pro.price.year",
      }),
      description: [
        intl.formatMessage({
          id: "subscription.pro.bulletpoint.one",
        }),
        intl.formatMessage({
          id: "subscription.pro.bulletpoint.two",
        }),
        intl.formatMessage({
          id: "subscription.pro.bulletpoint.three",
        }),
      ],
      buttonText: intl.formatMessage({
        id: "subscription.pro.button",
      }),
      buttonText2: intl.formatMessage({
        id: "subscription.pro.button.two",
      }),
      buttonVariant: "contained",
    },
  ]

  const onClickPricingBtn = () => {
    logEvent(analytics, "web_download_app_clicked")
    window.open(urlGooglePlay)
  }

  const onClickBKashBtn = (title: String) => {
    logEvent(analytics, "web_bkash_payment_clicked")
    if (isProSubscription(title)) {
      setSelectedProduct(Product.Pro)
    } else {
      setSelectedProduct(Product.AdFree)
    }
    setOpenBKashDialog(true)
  }

  return (
    <React.Fragment>
      <GlobalStyles
        styles={{ ul: { margin: 0, padding: 0, listStyle: "none" } }}
      />
      <CssBaseline />
      <Container
        disableGutters
        maxWidth="sm"
        component="main"
        sx={{ pt: 8, pb: 6 }}
      >
        {caller === Caller.Homepage && (
          <Typography
            fontSize={Dimen.FontSizeHeading}
            align="center"
            color="text.primary"
            gutterBottom
          >
            <FormattedMessage id="pricing.title" />
          </Typography>
        )}
      </Container>
      <Container maxWidth="lg" component="main">
        <Grid container spacing={7} alignItems="flex-end">
          {tiers.map((tier) => (
            <Grid key={tier.title} size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <Card style={{ border: `1px solid ${Color.PrimaryLight85}` }}>
                <CardHeader
                  title={tier.title}
                  subheader={tier.subheader}
                  slotProps={{
                    title: { align: "center" },
                    subheader: { align: "center" },
                  }}
                  action={isProSubscription(tier.title) ? <StarIcon /> : null}
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === "light"
                        ? Color.PrimaryLight85
                        : theme.palette.grey[700],
                  }}
                />
                <CardContent>
                  {tier.priceLifetime && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "baseline",
                      }}
                    >
                      <Typography
                        component="h2"
                        variant="h3"
                        color="text.primary"
                      >
                        {tier.priceLifetime}
                      </Typography>

                      <Typography variant="h6" color="text.secondary">
                        <FormattedMessage id="subscription.cost.unit.lifetime" />
                      </Typography>
                    </Box>
                  )}

                  {tier.priceMonthly && (
                    <div>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "baseline",
                        }}
                      >
                        <Typography
                          component="h2"
                          variant="h6"
                          color="text.primary"
                        >
                          <FormattedMessage id="or" />
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "baseline",
                          mb: 2,
                        }}
                      >
                        <Typography
                          component="h2"
                          variant="h3"
                          color="text.primary"
                        >
                          {tier.priceMonthly}
                        </Typography>

                        <Typography variant="h6" color="text.secondary">
                          <FormattedMessage id="subscription.cost.unit" />
                        </Typography>
                      </Box>
                    </div>
                  )}

                  <ul>
                    {tier.description.map((line) => (
                      <Typography
                        component="li"
                        variant="subtitle1"
                        align="center"
                        key={line}
                      >
                        {line}
                      </Typography>
                    ))}
                  </ul>
                </CardContent>

                {caller === Caller.Homepage && (
                  <CardActions>
                    <Button
                      style={{
                        background: Color.Primary,
                        color: Color.Black,
                        border: "none",
                      }}
                      fullWidth
                      variant={tier.buttonVariant as "outlined" | "contained"}
                      onClick={() => onClickPricingBtn()}
                    >
                      {tier.buttonText}
                    </Button>
                  </CardActions>
                )}

                {!isFreeSubscription(tier.title) && (
                  <CardActions>
                    <Button
                      style={{
                        background: Color.BKash,
                        color: Color.White,
                        border: "none",
                      }}
                      fullWidth
                      variant={tier.buttonVariant as "outlined" | "contained"}
                      onClick={() => onClickBKashBtn(tier.title)}
                    >
                      {tier.buttonText2}
                    </Button>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* BKash dialog */}
      <Dialog
        open={openBKashDialog}
        slots={{ transition: Slide }}
        slotProps={{
          transition: {
            direction: "up",
          },
        }}
        keepMounted
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            setOpenBKashDialog(false)
          }
        }}
      >
        <BKashDialog
          product={selectedProduct}
          onClickClose={() => {
            setOpenBKashDialog(false)
          }}
          email={email}
        ></BKashDialog>
      </Dialog>
    </React.Fragment>
  )
}

export default Pricing
